<template>
  <div class="public-lead-page" :style="pageStyle">
    <div v-if="loading" class="public-lead-state">
      <div class="public-lead-state__card">
        <div class="public-lead-spinner" aria-hidden="true" />
        <p>{{ t('publicForm.loading') }}</p>
      </div>
    </div>

    <div v-else-if="loadError" class="public-lead-state">
      <div class="public-lead-state__card">
        <h1>{{ t('publicForm.unavailableTitle') }}</h1>
        <p>{{ loadError }}</p>
      </div>
    </div>

    <div v-else-if="submitted" class="public-lead-state">
      <div class="public-lead-layout public-lead-layout--success">
        <aside class="public-lead-brand">
          <div class="public-lead-brand__bg" />
          <div class="public-lead-brand__content public-lead-brand__content--compact">
            <div class="public-lead-brand__hero">
              <div class="public-lead-brand__avatar-shell">
                <span class="public-lead-brand__avatar-glow" aria-hidden="true" />
                <span class="public-lead-brand__avatar-ring" aria-hidden="true" />
                <div class="public-lead-brand__avatar-wrap">
                  <img
                    v-if="workspacePhotoUrl"
                    :src="workspacePhotoUrl"
                    :alt="workspaceName"
                    class="public-lead-brand__avatar"
                  />
                  <span v-else class="public-lead-brand__avatar-fallback">{{ workspaceInitials }}</span>
                </div>
              </div>
              <p v-if="workspaceName" class="public-lead-brand__workspace">
                <span class="public-lead-brand__workspace-badge">{{ workspaceName }}</span>
              </p>
            </div>
          </div>
        </aside>
        <div class="public-lead-form-area public-lead-form-area--center">
          <div class="public-lead-success-icon" aria-hidden="true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 class="public-lead-form-area__heading">{{ t('publicForm.successTitle') }}</h2>
          <p class="public-lead-success-message">{{ successMessage }}</p>
        </div>
      </div>
    </div>

    <div v-else class="public-lead-page__shell">
      <div class="public-lead-layout">
        <aside class="public-lead-brand">
          <div class="public-lead-brand__bg" />
          <div class="public-lead-brand__content">
            <div class="public-lead-brand__hero">
              <div class="public-lead-brand__avatar-shell">
                <span class="public-lead-brand__avatar-glow" aria-hidden="true" />
                <span class="public-lead-brand__avatar-ring" aria-hidden="true" />
                <div class="public-lead-brand__avatar-wrap">
                  <img
                    v-if="workspacePhotoUrl"
                    :src="workspacePhotoUrl"
                    :alt="workspaceName"
                    class="public-lead-brand__avatar"
                  />
                  <span v-else class="public-lead-brand__avatar-fallback">{{ workspaceInitials }}</span>
                </div>
              </div>
              <p v-if="workspaceName" class="public-lead-brand__workspace">
                <span class="public-lead-brand__workspace-badge">{{ workspaceName }}</span>
              </p>
            </div>
            <div class="public-lead-brand__body">
              <h1 class="public-lead-brand__title">{{ formName }}</h1>
              <p class="public-lead-brand__tagline">{{ t('publicForm.tagline') }}</p>
              <span class="public-lead-brand__secure">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                {{ t('publicForm.secureNote') }}
              </span>
            </div>
          </div>
        </aside>

        <div class="public-lead-form-area">
          <h2 class="public-lead-form-area__heading">{{ t('publicForm.fillTitle') }}</h2>
          <p class="public-lead-form-area__sub">{{ t('publicForm.fillSubtitle') }}</p>

          <form class="public-lead-fields" novalidate @submit.prevent="submit">
            <div
              v-for="field in visibleFields"
              :key="field.key"
              class="public-lead-field"
            >
              <label class="public-lead-field__label" :for="`plf-${field.key}`">
                {{ field.label }}
                <span v-if="field.required" class="public-lead-field__required">*</span>
              </label>
              <div
                class="public-lead-input-wrap"
                :class="{ 'public-lead-input-wrap--error': errors[field.key] }"
              >
                <span class="public-lead-input-wrap__icon" v-html="field.iconSvg" />
                <input
                  :id="`plf-${field.key}`"
                  v-model="form[field.key]"
                  :type="field.inputType"
                  :inputmode="field.inputMode"
                  :autocomplete="field.autocomplete"
                  :placeholder="field.placeholder"
                  @blur="touch(field.key)"
                  @input="onFieldInput(field.key)"
                />
              </div>
              <p v-if="errors[field.key]" class="public-lead-field__error">
                {{ errorMessage(errors[field.key]) }}
              </p>
            </div>

            <p v-if="submitError" class="public-lead-alert public-lead-alert--error" role="alert">
              {{ submitError }}
            </p>

            <button type="submit" class="public-lead-submit" :disabled="submitting">
              {{ submitting ? t('publicForm.submitting') : t('publicForm.submit') }}
            </button>
          </form>
        </div>
      </div>
    </div>

    <footer class="public-lead-footer">
      {{ t('publicForm.poweredBy') }}
      <a href="/" target="_blank" rel="noopener">BackLog</a>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, reactive } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import publicApi from '@/services/publicApi'
import { resolveMediaUrl } from '@/config/app'
import {
  validateEmail,
  validatePhone,
  validateRequiredText,
  validateCnpj,
  formatPhoneBr,
  formatCnpj
} from '@/utils/publicFormValidation'

const { t } = useI18n()
const route = useRoute()
const token = route.params.token

const ICONS = {
  nome: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>',
  empresa: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>',
  cargo: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2h8z"/></svg>',
  whatsapp: '<svg fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>',
  email: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>',
  cnpj: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>'
}

const FIELD_META = {
  nome: { inputType: 'text', inputMode: 'text', autocomplete: 'name' },
  empresa: { inputType: 'text', inputMode: 'text', autocomplete: 'organization' },
  cargo: { inputType: 'text', inputMode: 'text', autocomplete: 'organization-title' },
  whatsapp: { inputType: 'tel', inputMode: 'tel', autocomplete: 'tel' },
  email: { inputType: 'email', inputMode: 'email', autocomplete: 'email' },
  cnpj: { inputType: 'text', inputMode: 'numeric', autocomplete: 'off' }
}

const loading = ref(true)
const loadError = ref('')
const formName = ref('')
const fields = ref({})
const workspaceName = ref('')
const workspacePhoto = ref('')
const workspaceColor = ref('#5865F2')
const successMessage = ref('')
const submitted = ref(false)
const submitting = ref(false)
const submitError = ref('')
const touched = reactive({})
const errors = reactive({})

const form = ref({
  nome: '',
  empresa: '',
  cargo: '',
  whatsapp: '',
  email: '',
  cnpj: ''
})

const pageStyle = computed(() => ({
  '--plf-accent': workspaceColor.value || '#5865F2'
}))

const workspacePhotoUrl = computed(() => {
  if (!workspacePhoto.value) return ''
  return resolveMediaUrl(workspacePhoto.value) || workspacePhoto.value
})

const workspaceInitials = computed(() => {
  const name = workspaceName.value || formName.value || 'B'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
})

const visibleFields = computed(() => {
  const order = ['nome', 'empresa', 'cargo', 'whatsapp', 'email', 'cnpj']
  return order
    .filter((key) => fields.value?.[key]?.enabled)
    .map((key) => {
      const optional = !!fields.value[key].optional
      return {
        key,
        label: t(`publicForm.fields.${key}`),
        required: !optional,
        iconSvg: ICONS[key],
        placeholder: t(`publicForm.placeholders.${key}`),
        ...FIELD_META[key]
      }
    })
})

function isEnabled (key) {
  return fields.value?.[key]?.enabled
}

function touch (key) {
  touched[key] = true
  validateField(key)
}

function validateField (key) {
  if (!isEnabled(key)) {
    errors[key] = null
    return null
  }
  const optional = !!fields.value[key]?.optional
  const required = !optional
  let err = null
  const val = form.value[key]
  switch (key) {
    case 'nome':
      err = validateRequiredText(val, true)
      break
    case 'empresa':
    case 'cargo':
      err = validateRequiredText(val, required)
      break
    case 'whatsapp':
      err = validatePhone(val, required)
      break
    case 'email':
      err = validateEmail(val, required)
      break
    case 'cnpj':
      err = validateCnpj(val, required)
      break
    default:
      break
  }
  errors[key] = err
  return err
}

function validateAll () {
  let ok = true
  for (const f of visibleFields.value) {
    touched[f.key] = true
    if (validateField(f.key)) ok = false
  }
  const hasEmail = isEnabled('email')
  const hasWhats = isEnabled('whatsapp')
  if (hasEmail || hasWhats) {
    const emailVal = (form.value.email || '').trim()
    const whatsVal = (form.value.whatsapp || '').trim()
    if (!emailVal && !whatsVal) {
      if (hasEmail) {
        errors.email = 'contact'
        touched.email = true
      }
      if (hasWhats) {
        errors.whatsapp = 'contact'
        touched.whatsapp = true
      }
      ok = false
    }
  }
  return ok
}

function errorMessage (code) {
  if (!code) return ''
  const map = {
    required: 'validationRequired',
    invalid: 'validationInvalid',
    contact: 'validationContact'
  }
  return t(`publicForm.${map[code] || 'validationInvalid'}`)
}

function onFieldInput (key) {
  if (key === 'whatsapp') {
    form.value.whatsapp = formatPhoneBr(form.value.whatsapp)
  }
  if (key === 'cnpj') {
    form.value.cnpj = formatCnpj(form.value.cnpj)
  }
  if (touched[key]) validateField(key)
}

async function load () {
  loading.value = true
  loadError.value = ''
  try {
    const { data } = await publicApi.get(`/public/forms/${token}`)
    formName.value = data.formName || t('publicForm.defaultTitle')
    fields.value = data.fields || {}
    successMessage.value = data.successMessage || t('publicForm.defaultSuccess')
    workspaceName.value = data.workspaceName || ''
    workspacePhoto.value = data.workspacePhoto || ''
    workspaceColor.value = data.workspaceColor || '#5865F2'
  } catch (e) {
    loadError.value = e.response?.data?.error || t('publicForm.unavailable')
  } finally {
    loading.value = false
  }
}

async function submit () {
  submitError.value = ''
  if (!validateAll()) return
  submitting.value = true
  try {
    const { data } = await publicApi.post(`/public/forms/${token}/submit`, { ...form.value })
    successMessage.value = data.message || successMessage.value
    submitted.value = true
  } catch (e) {
    if (e.response?.status === 429) {
      submitError.value = t('publicForm.rateLimit')
    } else {
      submitError.value = e.response?.data?.error || t('publicForm.submitError')
    }
  } finally {
    submitting.value = false
  }
}

onMounted(load)
</script>

<style src="@/assets/styles/public-lead-form.css"></style>
